/**
 * useGmailAuth — manages Gmail OAuth via Google Identity Services (GIS).
 *
 * Uses the Authorization Code flow (confidential client — the code is exchanged
 * on the backend with the client secret, which is why Google issues a refresh
 * token). Not PKCE: PKCE is for public clients that cannot hold a secret, and
 * the secret never leaves the server here. CSRF is handled by the `state`
 * nonce below, per RFC 6749 §10.12.
 * The backend stores both tokens AES-256-GCM encrypted.
 *
 * This hook tracks CONNECTION STATE only — it never holds a Google token. The
 * token lives server-side; Gmail is reached through the backend proxy. So
 * `isConnected` comes from /token/status, not from possessing a credential.
 *
 * On mount: checks backend for an existing connection.
 * On connect: redirects through Google consent with access_type=offline.
 * On disconnect: backend revokes at Google, then deletes.
 *
 * Auto-refresh: asks the backend to refresh 5 minutes before expiry so the
 * stored token never lapses while the page is open.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { GMAIL_SCOPES, GMAIL_SCOPES_FULL, OAUTH_STATE_KEY, OAUTH_CLIENT_KEY_STORAGE } from '../constants';
import {
  getTokenStatus,
  refreshGmailToken,
  disconnectGmail,
  getOAuthConfig,
} from '../engine/backendClient';

const FALLBACK_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID_EMAIL_INSIGHTS;
const FALLBACK_REDIRECT_URI = process.env.REACT_APP_GOOGLE_REDIRECT_URI_EMAIL_INSIGHTS || `${window.location.origin}/google/callback`;

// How many ms before expiry to proactively refresh (5 minutes)
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

/**
 * 128-bit random `state` nonce, hex encoded. Google echoes it back on the
 * redirect; GoogleCallback refuses to exchange a code whose state does not
 * match the one this tab minted. Without it, an attacker could hand a victim a
 * consent URL and have the resulting authorization code land in the victim's
 * session (RFC 6749 §10.12).
 */
function generateOAuthState() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function useGmailAuth() {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectedAt, setConnectedAt] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [accessMode, setAccessMode] = useState('metadata');
  const [needsFullAccess, setNeedsFullAccess] = useState(false);
  const [wantsFullAccess, setWantsFullAccess] = useState(false);

  // Ref to the scheduled refresh timer so we can cancel it on unmount/disconnect
  const refreshTimerRef = useRef(null);

  // ── Schedule a proactive token refresh ───────────────────────────────────
  const scheduleRefresh = useCallback((tokenExpiresAt) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (!tokenExpiresAt) return;

    const msUntilRefresh = new Date(tokenExpiresAt).getTime() - Date.now() - REFRESH_BUFFER_MS;
    if (msUntilRefresh <= 0) {
      // Already close to expiry — refresh immediately
      doRefresh();
      return;
    }

    refreshTimerRef.current = setTimeout(() => {
      doRefresh();
    }, msUntilRefresh);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Ask the backend to refresh the stored token ───────────────────────────
  // The response carries only the new expiry — the token itself stays server-side.
  const doRefresh = useCallback(async () => {
    try {
      const result = await refreshGmailToken();
      if (result.needsReconnect) {
        // Google rejected the refresh token — user must reconnect
        setConnected(false);
        setConnectedAt(null);
        setExpiresAt(null);
        return;
      }
      setExpiresAt(result.expiresAt);
      scheduleRefresh(result.expiresAt);
    } catch {
      // Refresh failed — stay connected; the next fetch will surface the error
    }
  }, [scheduleRefresh]);

  // ── On mount: check backend for existing Gmail connection ─────────────────
  useEffect(() => {
    let cancelled = false;

    // 3 second timeout — if backend is slow/down, don't block the UI
    const timeout = setTimeout(() => {
      if (!cancelled) {
        cancelled = true;
        setLoading(false);
      }
    }, 3000);

    async function checkExisting() {
      try {
        try {
          const cfg = await getOAuthConfig();
          if (!cancelled && cfg?.accessMode === 'full') setWantsFullAccess(true);
        } catch {
          // ignore — Connect screen stays metadata copy
        }
        const status = await getTokenStatus();
        if (cancelled) return;

        if (status.connected) {
          const isExpired = status.expiresAt && new Date(status.expiresAt) < new Date();

          if (isExpired && !status.hasRefreshToken) {
            // Expired with no way to refresh — clean up, user must reconnect
            disconnectGmail().catch(() => {});
          } else {
            // Connected. If the stored token has lapsed, the backend refreshes
            // it on the next proxied call, so there is nothing to do here
            // beyond keeping the proactive refresh timer armed.
            setConnected(true);
            setConnectedAt(status.connectedAt);
            setExpiresAt(status.expiresAt);
            setAccessMode(status.accessMode === 'full' ? 'full' : 'metadata');
            setNeedsFullAccess(!!status.needsFullAccess);
            scheduleRefresh(status.expiresAt);
          }
        }
      } catch {
        // Backend unreachable or not connected — show Connect screen
      } finally {
        if (!cancelled) {
          clearTimeout(timeout);
          setLoading(false);
        }
      }
    }

    checkExisting();
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Clean up refresh timer on unmount ─────────────────────────────────────
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  // ── Load GIS script ───────────────────────────────────────────────────────
  useEffect(() => {
    if (window.google?.accounts?.oauth2) return;
    const existingScript = document.getElementById('gis-script');
    if (existingScript) return;

    const script = document.createElement('script');
    script.id = 'gis-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }, []);

  // ── Connect: use Code flow so Google issues a refresh token ───────────────
  // ADDITIVE: ask backend which clientId to use (Ergode multi-client fill-order).
  // Non-Ergode / API failure → fall back to build-time env client (unchanged).
  const connect = useCallback(async () => {
    if (!window.google?.accounts?.oauth2) {
      setError('Google Identity Services not loaded yet. Please try again.');
      return;
    }
    setError(null);

    let clientId = FALLBACK_CLIENT_ID;
    let redirectUri = FALLBACK_REDIRECT_URI;
    let oauthClientKey = 'default';
    let scope = GMAIL_SCOPES;
    try {
      const cfg = await getOAuthConfig();
      if (cfg?.clientId) clientId = cfg.clientId;
      if (cfg?.redirectUri) redirectUri = cfg.redirectUri;
      if (cfg?.oauthClientKey) oauthClientKey = cfg.oauthClientKey;
      if (cfg?.scope) scope = cfg.scope;
      if (cfg?.accessMode === 'full') {
        scope = cfg.scope || GMAIL_SCOPES_FULL;
        setWantsFullAccess(true);
      }
    } catch {
      // Keep fallback — non-Ergode and any backend blip still work as before
    }

    if (!clientId) {
      setError('Gmail OAuth is not configured. Please contact support.');
      return;
    }

    /**
     * initCodeClient uses the Authorization Code flow with redirect mode.
     * Google redirects the user to REDIRECT_URI with the authorization code
     * and our `state` echoed back. The callback page verifies state, then
     * sends the code to our backend which exchanges it for both the access
     * token and the refresh token using the client secret.
     */
    const state = generateOAuthState();
    try {
      sessionStorage.setItem(OAUTH_STATE_KEY, state);
      sessionStorage.setItem(OAUTH_CLIENT_KEY_STORAGE, oauthClientKey);
    } catch {
      // Private browsing / storage disabled — without somewhere to keep the
      // nonce we cannot verify the callback, so refuse rather than fall back
      // to an unprotected flow.
      setError('Your browser is blocking session storage, which is required to connect Gmail securely. Please disable private browsing and try again.');
      return;
    }

    const codeClient = window.google.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope,
      ux_mode: 'redirect',
      redirect_uri: redirectUri,
      state,
    });

    codeClient.requestCode();
  }, []);

  const disconnect = useCallback(async () => {
    // Cancel any pending refresh
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Revocation is the backend's job now — it holds the token and calls
    // Google's revoke endpoint before deleting. The client no longer has a
    // token to revoke, and doing it here was best-effort anyway.
    setConnected(false);
    setConnectedAt(null);
    setExpiresAt(null);
    setAccessMode('metadata');
    setNeedsFullAccess(false);
    setError(null);

    try {
      await disconnectGmail();
    } catch (err) {
      console.warn('[useGmailAuth] Failed to disconnect from backend:', err.message);
    }
  }, []);

  /**
   * Local-only: grant already gone on the server (410 from Gmail proxy).
   * Flip UI to Connect screen — do not show Connected.
   */
  const markExpired = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    setConnected(false);
    setConnectedAt(null);
    setExpiresAt(null);
    setAccessMode('metadata');
    setNeedsFullAccess(false);
    setError('Gmail connection expired — please reconnect.');
  }, []);

  return {
    isConnected: connected,
    loading,
    error,
    connect,
    disconnect,
    markExpired,
    connectedAt,
    expiresAt,
    accessMode,
    needsFullAccess,
    wantsFullAccess,
  };
}
